/**
 * 推薦碼 × 業務歸屬 × 月結分潤(R392 建;admin 面 A1–A23)— wire DTO + domain + mapper
 * -----------------------------------------------------------------------------
 * 契約 SoT = BE `docs/128-frontend-referral-commission.md`(ADR-049)+ 設計書
 * `docs/architecture/referral-commission-design.md` §6/§9;wire 欄位逐欄對帳 BE
 * `app/referral/{schemas,models}.py`(branch `74dfb3c2`,MR !121;dev 未部署前打 API 404)。
 *
 * 業務語意:
 *   - 客戶 = 工作區(group);業務(sales rep)發推薦碼綁定客戶,系統每日彙整
 *     「已消耗點數」,每月產對帳單。**分潤 = 消耗點數 × 費率,以「點」計,無單價層**;
 *     費率一律 bps 整數(90 = 0.90%,FE 以 % 輸入 ×100 轉整數送出)。
 *   - **FE 不做任何加總**(docs/128 §4-2):總額一律用後端 `total_*` —— 自己加會與
 *     逐行 HALF_UP 進位差幾點。
 *   - 綁定是台北日粒度的左閉右開區間(append-only);`effective_from`/`effective_to`
 *     是 `YYYY-MM-DD` **純字串**,顯示原樣輸出 —— 過 `new Date()` 會被裝置時區挪日
 *     (R391 教訓)。期別 `YYYY-MM` 同理,**月份選擇器只能吃 A22**,不得裝置時區自推。
 *   - `HOUSE`(內部/直客)是保留字系統業務/系統碼:一切修改 BE 403
 *     `REFERRAL_HOUSE_PROTECTED` → UI 唯讀呈現(不畫任何寫入鈕)。
 *   - 推薦碼正規形不含字母 I/L/O/U(`HAI2026` 存成 `HA12026`):顯示一律用後端
 *     回傳值,FE 不改寫、不自行正規化。
 *
 * ⚠️ 狀態欄(binding / statement / rep / code)domain 型別刻意是開放 `string`
 * (R296:wire 值域是開放的),已知值集另以 `*_STATUSES` 常數供 filter / pill 查表,
 * 呈現端查不到要顯 wire 原值,不可白屏。
 */

// ─── 已知值集(filter / pill 用;wire 欄位本身開放)────────────────────────────

export const HOUSE_REP_ID = 'HOUSE';

export type SalesRepStatus = 'ACTIVE' | 'INACTIVE';
export const SALES_REP_STATUSES: readonly SalesRepStatus[] = ['ACTIVE', 'INACTIVE'];

export type ReferralCodeStatus = 'ACTIVE' | 'DISABLED';
export const REFERRAL_CODE_STATUSES: readonly ReferralCodeStatus[] = ['ACTIVE', 'DISABLED'];

export type ReferralBindingStatus = 'ACTIVE' | 'CLOSED' | 'VOID';

export type ReferralStatementStatus = 'DRAFT' | 'LOCKED' | 'PAID';
export const REFERRAL_STATEMENT_STATUSES: readonly ReferralStatementStatus[] = [
  'DRAFT',
  'LOCKED',
  'PAID',
];

/** A23 `action` 已知值集(BE `AuditAction` Literal;查詢 filter 用)。 */
export const REFERRAL_AUDIT_ACTIONS = [
  'REP_CREATE',
  'REP_UPDATE',
  'CODE_CREATE',
  'CODE_UPDATE',
  'BIND',
  'REPLACE',
  'VOID',
  'LOCK',
  'UNLOCK_VOID',
  'MARK_PAID',
  'ADJUST',
  'BULK_REASSIGN',
  'RECOMPUTE',
  'EXPORT',
] as const;

export type ReferralAuditTargetKind = 'rep' | 'code' | 'binding' | 'statement' | 'period';
export const REFERRAL_AUDIT_TARGET_KINDS: readonly ReferralAuditTargetKind[] = [
  'rep',
  'code',
  'binding',
  'statement',
  'period',
];

// ─── wire DTOs(對齊 BE schemas.py / models.py)────────────────────────────────

/** BE `RepView`(A1/A3/A4 回應、A2 清單單筆)。 */
export interface SalesRepDto {
  rep_id: string;
  name: string;
  contact_email?: string | null;
  linked_cuid?: string | null;
  status: string;
  default_commission_bps: number;
  note?: string | null;
  version?: number;
}

export interface SalesRepListDto {
  reps: SalesRepDto[];
  next_cursor?: string | null;
}

/** BE `PeriodBriefView`(A3 本期概況;月結 cron 沒跑過 → status null、數字 0)。 */
export interface RepPeriodBriefDto {
  period: string;
  status?: string | null;
  total_consumed_points: number;
  total_commission_points: number;
}

/** BE `CodeView`(A5/A6/A7)。`active_binding_count` 是即時 count,不是 denorm 欄。 */
export interface ReferralCodeDto {
  code: string;
  rep_id: string;
  label?: string | null;
  status: string;
  commission_bps_override?: number | null;
  max_bindings?: number | null;
  is_house?: boolean;
  active_binding_count: number;
  version?: number;
}

export interface ReferralCodeListDto {
  codes: ReferralCodeDto[];
  next_cursor?: string | null;
}

export interface RepDetailDto {
  rep: SalesRepDto;
  codes: ReferralCodeDto[];
  current_period: RepPeriodBriefDto;
}

/** BE `CodeUpdateResponse`(A7)。`affects_future_only` 恆 true — 改隸屬不回溯。 */
export interface CodeUpdateResultDto {
  code: ReferralCodeDto;
  affects_future_only?: boolean;
}

/** BE `CustomerRow`(A8)。code/rep null = 未歸屬(**要顯「未歸屬」不留空白**)。 */
export interface ReferralCustomerRowDto {
  group_id: string;
  customer_name: string;
  code?: string | null;
  rep_id?: string | null;
  rep_name?: string | null;
  effective_from?: string | null;
  /** 生效日 date-picker 的下限(§5.1;null = 無限制)。 */
  min_effective_from?: string | null;
}

export interface ReferralCustomersDto {
  customers: ReferralCustomerRowDto[];
  next_cursor?: string | null;
}

/** BE `BindingView`(A12 歷史列)。`created_by_cuid` = 操作人(SELF_SIGNUP = 客戶自綁)。 */
export interface ReferralBindingDto {
  binding_id: string;
  group_id: string;
  code: string;
  rep_id: string;
  rep_name?: string | null;
  effective_from: string;
  effective_to?: string | null;
  status: string;
  source: string;
  reason?: string | null;
  void_reason?: string | null;
  created_by_cuid: string;
}

export interface ReferralBindingHistoryDto {
  bindings: ReferralBindingDto[];
}

/** BE `AdminBindResponse`(A9)。 */
export interface AdminBindResultDto {
  binding_id: string;
  group_id: string;
  code: string;
  rep_id: string;
  rep_name?: string | null;
  effective_from: string;
  effective_to?: string | null;
  replaced?: {
    binding_id: string;
    code: string;
    effective_to?: string | null;
    /** true = 未生效的舊指標被作廢;false = 正常關閉(左閉右開)。 */
    voided?: boolean;
  } | null;
  min_effective_from?: string | null;
}

/**
 * BE `bulk_reassign` 回應(A10)。dry-run 與 apply 兩形狀;`count_mismatch` 形狀
 * **不會**以 200 出現(router 轉 409 `REFERRAL_BULK_COUNT_MISMATCH`)。
 */
export interface BulkReassignResultDto {
  dry_run: boolean;
  count?: number;
  bindings?: { group_id: string; binding_id: string; code: string }[];
  applied?: string[];
  failed?: { group_id: string; error: string }[];
  skipped?: string[];
}

/** BE `ReferralPeriodSummaryEntity`(A14 單筆 / A22 清單單筆)。 */
export interface ReferralPeriodSummaryDto {
  period: string;
  total_consumed_points?: number;
  attributed_points?: number;
  unattributed_points?: number;
  rep_count?: number;
  statement_count?: number;
  /** true = 該期有 rollup 未完成日 → A17 禁鎖帳。 */
  incomplete?: boolean;
  computed_at?: string | null;
  status?: string;
}

/** BE `CommissionStatementEntity`(A15 清單單筆 / A16 主體)。 */
export interface ReferralStatementDto {
  period: string;
  rep_id: string;
  rep_name: string;
  status: string;
  revision: number;
  total_consumed_points?: number;
  total_commission_points?: number;
  adjustment_points?: number;
  payable_points?: number;
  /** payable 被 clamp 到 0(total + adjustment < 0)時 true。 */
  clamped?: boolean;
  incomplete?: boolean;
  line_count?: number;
  computed_at?: string | null;
  locked_at?: string | null;
  locked_by_cuid?: string | null;
  paid_at?: string | null;
  paid_by_cuid?: string | null;
  void_reason?: string | null;
}

/** BE `StatementLineEntity`(A16 `lines[]`)。 */
export interface ReferralStatementLineDto {
  revision: number;
  code: string;
  group_id: string;
  customer_name: string;
  binding_ids?: string[];
  binding_days: number;
  active_days: number;
  first_day?: string | null;
  last_day?: string | null;
  consumed_points: number;
  commission_bps: number;
  commission_points: number;
}

/** BE `StatementAdjustmentEntity`(A16 `adjustments[]`)。 */
export interface ReferralAdjustmentDto {
  adj_id: string;
  amount_points: number;
  reason: string;
  ref_period?: string | null;
  created_by_cuid?: string | null;
  created_at?: string | null;
}

export interface ReferralStatementDetailDto extends ReferralStatementDto {
  lines?: ReferralStatementLineDto[];
  adjustments?: ReferralAdjustmentDto[];
}

/** A13 重算回應(BE `run_statement_draft` summary dict)。 */
export interface RecomputePeriodResultDto {
  period?: string;
  statements_written?: number;
  skipped_locked?: number;
  /** 已鎖跳過的業務(顯示給 admin — 這些單要先 void 才會被重算)。 */
  skipped_reps?: string[];
  total_consumed_points?: number;
  unattributed_points?: number;
  incomplete?: boolean;
}

/**
 * C2 `GET /referral/current` 回應(BE `CurrentBindingResponse`)。
 * **刻意沒有** label / 費率 / rep_id(內部資訊不回客戶端)— 不要向 BE 要。
 */
export interface ReferralCurrentBindingDto {
  bound: boolean;
  code?: string | null;
  rep_name?: string | null;
  /** 未生效的綁定也回(顯示「自 X 起生效」)。 */
  effective_from?: string | null;
  bound_at?: string | null;
}

/** C1 `POST /referral/bind` 回應(BE router 實形:`{code, effective_from}`)。 */
export interface SelfBindResultDto {
  code: string;
  effective_from?: string | null;
}

/** A23 稽核列(BE `ReferralAuditLogEntity` doc 原樣)。 */
export interface ReferralAuditLogDto {
  log_id: string;
  actor_cuid?: string | null;
  action?: string | null;
  target_kind?: string | null;
  target_id?: string | null;
  reason?: string | null;
  created_at?: string | null;
}

export interface ReferralAuditLogsDto {
  logs: ReferralAuditLogDto[];
  next_cursor?: string | null;
}

// ─── domain ───────────────────────────────────────────────────────────────────

export interface SalesRep {
  repId: string;
  name: string;
  contactEmail: string | null;
  linkedCuid: string | null;
  /** 開放 string(R296);已知值 = `SALES_REP_STATUSES`。 */
  status: string;
  defaultCommissionBps: number;
  note: string;
}

export interface SalesRepList {
  reps: SalesRep[];
  /** null = 已翻完(判準是它,不是「筆數 < limit」— R241 B2)。 */
  nextCursor: string | null;
}

export interface ReferralCode {
  code: string;
  repId: string;
  label: string;
  status: string;
  commissionBpsOverride: number | null;
  maxBindings: number | null;
  isHouse: boolean;
  activeBindingCount: number;
}

export interface ReferralCodeList {
  codes: ReferralCode[];
  nextCursor: string | null;
}

export interface RepPeriodBrief {
  period: string;
  status: string | null;
  totalConsumedPoints: number;
  totalCommissionPoints: number;
}

export interface RepDetail {
  rep: SalesRep;
  codes: ReferralCode[];
  currentPeriod: RepPeriodBrief;
}

export interface ReferralCustomerRow {
  groupId: string;
  customerName: string;
  /** null = 未歸屬(UI 顯「未歸屬」badge,不留空白)。 */
  code: string | null;
  repId: string | null;
  repName: string | null;
  effectiveFrom: string | null;
  minEffectiveFrom: string | null;
}

export interface ReferralCustomerList {
  customers: ReferralCustomerRow[];
  nextCursor: string | null;
}

export interface ReferralBinding {
  bindingId: string;
  groupId: string;
  code: string;
  repId: string;
  repName: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: string;
  source: string;
  reason: string;
  voidReason: string | null;
  createdByCuid: string;
}

export interface AdminBindResult {
  bindingId: string;
  groupId: string;
  code: string;
  repId: string;
  repName: string | null;
  effectiveFrom: string;
  replaced: { bindingId: string; code: string; effectiveTo: string | null; voided: boolean } | null;
  minEffectiveFrom: string | null;
}

export interface BulkReassignResult {
  dryRun: boolean;
  /** dry-run:會改到的綁定(applied 形狀時為空陣列)。 */
  count: number;
  bindings: { groupId: string; bindingId: string; code: string }[];
  applied: string[];
  failed: { groupId: string; error: string }[];
}

export interface ReferralPeriodSummary {
  period: string;
  totalConsumedPoints: number;
  attributedPoints: number;
  unattributedPoints: number;
  repCount: number;
  statementCount: number;
  incomplete: boolean;
  status: string;
}

export interface ReferralStatementRow {
  period: string;
  repId: string;
  repName: string;
  status: string;
  revision: number;
  totalConsumedPoints: number;
  totalCommissionPoints: number;
  adjustmentPoints: number;
  payablePoints: number;
  clamped: boolean;
  incomplete: boolean;
  lineCount: number;
  lockedAt: string | null;
  paidAt: string | null;
  voidReason: string | null;
}

export interface ReferralStatementLine {
  code: string;
  groupId: string;
  customerName: string;
  bindingDays: number;
  activeDays: number;
  consumedPoints: number;
  commissionBps: number;
  commissionPoints: number;
}

export interface ReferralAdjustment {
  adjId: string;
  amountPoints: number;
  reason: string;
  refPeriod: string | null;
  createdAt: string | null;
}

export interface ReferralStatementDetail extends ReferralStatementRow {
  lines: ReferralStatementLine[];
  adjustments: ReferralAdjustment[];
}

export interface RecomputePeriodResult {
  period: string | null;
  statementsWritten: number;
  skippedReps: string[];
  incomplete: boolean;
}

/** 目前工作區的綁定(C2;客戶面)。 */
export interface ReferralCurrentBinding {
  bound: boolean;
  code: string | null;
  repName: string | null;
  effectiveFrom: string | null;
  boundAt: string | null;
}

/** C1 自助首綁結果。 */
export interface SelfBindResult {
  code: string;
  effectiveFrom: string | null;
}

export interface ReferralAuditLog {
  logId: string;
  actorCuid: string | null;
  action: string | null;
  targetKind: string | null;
  targetId: string | null;
  reason: string;
  createdAt: string | null;
}

export interface ReferralAuditLogList {
  logs: ReferralAuditLog[];
  nextCursor: string | null;
}

// ─── mappers(邊界 normalize)───────────────────────────────────────────────────

export function mapSalesRepDto(dto: SalesRepDto): SalesRep {
  return {
    repId: dto.rep_id,
    name: dto.name,
    contactEmail: dto.contact_email ?? null,
    linkedCuid: dto.linked_cuid ?? null,
    status: dto.status,
    defaultCommissionBps: dto.default_commission_bps,
    note: dto.note ?? '',
  };
}

export function mapSalesRepListDto(dto: SalesRepListDto): SalesRepList {
  return { reps: (dto.reps ?? []).map(mapSalesRepDto), nextCursor: dto.next_cursor ?? null };
}

export function mapReferralCodeDto(dto: ReferralCodeDto): ReferralCode {
  return {
    code: dto.code,
    repId: dto.rep_id,
    label: dto.label ?? '',
    status: dto.status,
    commissionBpsOverride: dto.commission_bps_override ?? null,
    maxBindings: dto.max_bindings ?? null,
    isHouse: dto.is_house === true,
    activeBindingCount: dto.active_binding_count,
  };
}

export function mapReferralCodeListDto(dto: ReferralCodeListDto): ReferralCodeList {
  return { codes: (dto.codes ?? []).map(mapReferralCodeDto), nextCursor: dto.next_cursor ?? null };
}

export function mapRepDetailDto(dto: RepDetailDto): RepDetail {
  return {
    rep: mapSalesRepDto(dto.rep),
    codes: (dto.codes ?? []).map(mapReferralCodeDto),
    currentPeriod: {
      period: dto.current_period.period,
      status: dto.current_period.status ?? null,
      totalConsumedPoints: dto.current_period.total_consumed_points,
      totalCommissionPoints: dto.current_period.total_commission_points,
    },
  };
}

export function mapReferralCustomerRowDto(dto: ReferralCustomerRowDto): ReferralCustomerRow {
  return {
    groupId: dto.group_id,
    customerName: dto.customer_name,
    code: dto.code ?? null,
    repId: dto.rep_id ?? null,
    repName: dto.rep_name ?? null,
    effectiveFrom: dto.effective_from ?? null,
    minEffectiveFrom: dto.min_effective_from ?? null,
  };
}

export function mapReferralCustomersDto(dto: ReferralCustomersDto): ReferralCustomerList {
  return {
    customers: (dto.customers ?? []).map(mapReferralCustomerRowDto),
    nextCursor: dto.next_cursor ?? null,
  };
}

export function mapReferralBindingDto(dto: ReferralBindingDto): ReferralBinding {
  return {
    bindingId: dto.binding_id,
    groupId: dto.group_id,
    code: dto.code,
    repId: dto.rep_id,
    repName: dto.rep_name ?? null,
    effectiveFrom: dto.effective_from,
    effectiveTo: dto.effective_to ?? null,
    status: dto.status,
    source: dto.source,
    reason: dto.reason ?? '',
    voidReason: dto.void_reason ?? null,
    createdByCuid: dto.created_by_cuid,
  };
}

export function mapAdminBindResultDto(dto: AdminBindResultDto): AdminBindResult {
  return {
    bindingId: dto.binding_id,
    groupId: dto.group_id,
    code: dto.code,
    repId: dto.rep_id,
    repName: dto.rep_name ?? null,
    effectiveFrom: dto.effective_from,
    replaced: dto.replaced
      ? {
          bindingId: dto.replaced.binding_id,
          code: dto.replaced.code,
          effectiveTo: dto.replaced.effective_to ?? null,
          voided: dto.replaced.voided === true,
        }
      : null,
    minEffectiveFrom: dto.min_effective_from ?? null,
  };
}

export function mapBulkReassignResultDto(dto: BulkReassignResultDto): BulkReassignResult {
  return {
    dryRun: dto.dry_run,
    count: dto.count ?? (dto.applied?.length ?? 0),
    bindings: (dto.bindings ?? []).map((b) => ({
      groupId: b.group_id,
      bindingId: b.binding_id,
      code: b.code,
    })),
    applied: dto.applied ?? [],
    failed: (dto.failed ?? []).map((f) => ({ groupId: f.group_id, error: f.error })),
  };
}

export function mapReferralPeriodSummaryDto(dto: ReferralPeriodSummaryDto): ReferralPeriodSummary {
  return {
    period: dto.period,
    totalConsumedPoints: dto.total_consumed_points ?? 0,
    attributedPoints: dto.attributed_points ?? 0,
    unattributedPoints: dto.unattributed_points ?? 0,
    repCount: dto.rep_count ?? 0,
    statementCount: dto.statement_count ?? 0,
    incomplete: dto.incomplete === true,
    status: dto.status ?? 'DRAFT',
  };
}

export function mapReferralStatementDto(dto: ReferralStatementDto): ReferralStatementRow {
  return {
    period: dto.period,
    repId: dto.rep_id,
    repName: dto.rep_name,
    status: dto.status,
    revision: dto.revision,
    totalConsumedPoints: dto.total_consumed_points ?? 0,
    totalCommissionPoints: dto.total_commission_points ?? 0,
    adjustmentPoints: dto.adjustment_points ?? 0,
    payablePoints: dto.payable_points ?? 0,
    clamped: dto.clamped === true,
    incomplete: dto.incomplete === true,
    lineCount: dto.line_count ?? 0,
    lockedAt: dto.locked_at ?? null,
    paidAt: dto.paid_at ?? null,
    voidReason: dto.void_reason ?? null,
  };
}

export function mapReferralStatementDetailDto(
  dto: ReferralStatementDetailDto,
): ReferralStatementDetail {
  return {
    ...mapReferralStatementDto(dto),
    lines: (dto.lines ?? []).map((l) => ({
      code: l.code,
      groupId: l.group_id,
      customerName: l.customer_name,
      bindingDays: l.binding_days,
      activeDays: l.active_days,
      consumedPoints: l.consumed_points,
      commissionBps: l.commission_bps,
      commissionPoints: l.commission_points,
    })),
    adjustments: (dto.adjustments ?? []).map((a) => ({
      adjId: a.adj_id,
      amountPoints: a.amount_points,
      reason: a.reason,
      refPeriod: a.ref_period ?? null,
      createdAt: a.created_at ?? null,
    })),
  };
}

export function mapRecomputePeriodResultDto(dto: RecomputePeriodResultDto): RecomputePeriodResult {
  return {
    period: dto.period ?? null,
    statementsWritten: dto.statements_written ?? 0,
    skippedReps: dto.skipped_reps ?? [],
    incomplete: dto.incomplete === true,
  };
}

export function mapReferralCurrentBindingDto(dto: ReferralCurrentBindingDto): ReferralCurrentBinding {
  return {
    bound: dto.bound === true,
    code: dto.code ?? null,
    repName: dto.rep_name ?? null,
    effectiveFrom: dto.effective_from ?? null,
    boundAt: dto.bound_at ?? null,
  };
}

export function mapSelfBindResultDto(dto: SelfBindResultDto): SelfBindResult {
  return { code: dto.code, effectiveFrom: dto.effective_from ?? null };
}

export function mapReferralAuditLogsDto(dto: ReferralAuditLogsDto): ReferralAuditLogList {
  return {
    logs: (dto.logs ?? []).map((l) => ({
      logId: l.log_id,
      actorCuid: l.actor_cuid ?? null,
      action: l.action ?? null,
      targetKind: l.target_kind ?? null,
      targetId: l.target_id ?? null,
      reason: l.reason ?? '',
      createdAt: l.created_at ?? null,
    })),
    nextCursor: dto.next_cursor ?? null,
  };
}

// ─── 顯示 / 輸入換算(docs/128 §4 顯示紀律)─────────────────────────────────────

/**
 * bps → `%` 字串(90 → `0.90%`;兩位小數,docs/128 §4-1)。
 * 全站顯示費率**只走這一支**,不要在呈現端各自 `/100`。
 */
export function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

/**
 * 費率輸入(% 字串,最多兩位小數)→ bps 整數;不合法回 null。
 * `Math.round` 吸掉 `0.9 × 100 = 90.00000000000001` 這類浮點誤差 —— 送 BE 的
 * 一律是整數 bps(0..10000)。
 */
export function percentInputToBps(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const v = Number.parseFloat(trimmed);
  if (!Number.isFinite(v) || v < 0 || v > 100) return null;
  return Math.round(v * 100);
}
